import { useState, useEffect, useCallback } from 'react';
import type { WebflowItem } from '../types';
import { apiClient } from '../api/client';

interface UseItemsResult {
  items: WebflowItem[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useItems(
  collectionId: string,
  search: string
): UseItemsResult {
  const [items, setItems] = useState<WebflowItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchItems = useCallback(async () => {
    if (!collectionId) {
      setItems([]);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const data = await apiClient.getItems(collectionId, search);
      setItems(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load items');
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  }, [collectionId, search]);

  useEffect(() => {
    const timeoutId = setTimeout(fetchItems, 300); // Debounce
    return () => clearTimeout(timeoutId);
  }, [fetchItems]);

  return { items, isLoading, error, refetch: fetchItems };
}
